import {
  DEFAULT_CINEMA_LANGUAGE,
  DEFAULT_CINEMA_HANDLER,
  CINEMA_LANGUAGES,
  isValidCinemaLang,
  isValidCinemaSlug,
  isValidCinemaPath,
} from "~/utils/constants";
import { navigateTo } from "#imports";
import { useLastVisitedLocationCookie } from "~/composables/useLastVisitedLocationCookie";
import { shouldSkipRedirectCheck } from "~/utils/shouldSkipRedirectCheck";
import { buildCinemaRedirectRest } from "~/utils/buildCinemaRedirectRest";
import { normalizeCinemaCookiePath } from "~/utils/normalizeCinemaCookiePath";

const createRedirectQuery = (
  originalQuery: Record<string, any>,
  needsFromRedirect: boolean,
) => {
  const query = { ...originalQuery };
  delete query.fromRedirect;
  if (needsFromRedirect) query.fromRedirect = "1";
  return query;
};

export default defineNuxtRouteMiddleware((to, from) => {
  if (shouldSkipRedirectCheck(to.path)) {
    return;
  }

  const lastVisitedLang = useCookie("lastVisitedLang", { readonly: true });
  const locationCookie = useLastVisitedLocationCookie();

  // Validated "/{lang}/{cinema}" from the cookie, or null when the cookie is missing or
  // stale (e.g. a removed/renamed cinema slug). Same normalization as readCinemaCookie in
  // the h3 handler — never redirect onto an unvalidated cookie value: with the preserved
  // deep link appended, a stale cookie would grow the URL on every pass (redirect loop)
  // instead of converging.
  const cookiePath = normalizeCinemaCookiePath(locationCookie.cookie.value);

  if (to.path?.includes?.("/undefined")) {
    const cleanPath = to.path
      .split("/")
      .filter((segment) => segment !== "undefined" && segment !== "")
      .join("/");

    const redirectPath = cleanPath.startsWith("/")
      ? cleanPath
      : `/${cleanPath}`;

    if (from.path === redirectPath) {
      return;
    }

    const pathSegments = redirectPath.split("/").filter(Boolean);
    const hasValidStructure =
      pathSegments.length >= 2 &&
      isValidCinemaPath(pathSegments[0], pathSegments[1]);

    if (hasValidStructure) {
      return navigateTo({
        path: redirectPath,
        query: createRedirectQuery(to.query, false),
      });
    } else if (cookiePath) {
      return navigateTo({
        path: cookiePath,
        query: createRedirectQuery(to.query, false),
      });
    } else {
      return navigateTo({
        path: `/${
          lastVisitedLang.value ?? DEFAULT_CINEMA_LANGUAGE
        }/${DEFAULT_CINEMA_HANDLER}`,
        query: createRedirectQuery(to.query, true),
      });
    }
  }

  const seg = to.path.split("/").filter(Boolean);

  // Path already has a valid /{lang}/{cinema} -> it is content (or a 404), never a
  // redirect. Parity with the h3 handler (server/middleware/02.cinemaRedirect.ts), which
  // lets such requests through to the Nuxt render on the same condition.
  //
  // Without this guard a path no route matches (i.e. deeper than
  // pages/[lang]/[cinema-slug]/[sub-page]/[sub2-page]/[sub3-page]) has no fixed point:
  // `to.params` is empty, so even a valid /cz/hradec/... falls into hasInvalidLanguage &&
  // hasInvalidCinemaSlug and gets redirected to the cookie/default cinema with the path
  // preserved including the cinema segment. Every pass then grows the URL by one segment
  // until it hits `to.path.length < 1000` -> a single 302 to a ~1 kB URL with 130+
  // repeated segments instead of a 404.
  if (isValidCinemaPath(seg[0], seg[1])) {
    return;
  }

  const hasInvalidLanguage =
    !isValidCinemaLang(to.params.lang) ||
    !Object.values(CINEMA_LANGUAGES).some(
      (lang) => to.path?.startsWith?.(`/${lang}`),
    );

  const hasInvalidCinemaSlug =
    !to.params?.cinemaslug || !isValidCinemaSlug(to.params.cinemaslug);

  // Preserve the deep link (drop only the lang prefix / "default" wildcard) — same
  // path-preserving logic as the h3 handler (server/middleware/02.cinemaRedirect.ts)
  // via the shared buildCinemaRedirectRest, so full page loads, SPA navigation and
  // the CINEMA_EDGE_REDIRECT=0 fallback all resolve a URL to the same target. Root
  // ("/") yields an empty rest -> cinema HP without a trailing slash (a trailing
  // slash would turn the redirect into a permanent 301 via the trailing-slash
  // middleware).
  const restOfPath = buildCinemaRedirectRest(seg);

  if (
    (!isValidCinemaLang(to.params.lang) || !to.params?.cinemaslug) &&
    !Object.values(CINEMA_LANGUAGES).some(
      (lang) => to.path?.startsWith?.(`/${lang}`),
    ) &&
    to.path?.length < 1000
  ) {
    // Check if the first path segment is a valid cinema slug (e.g. /olomouc, /olomouc/program)
    // If so, the user is navigating to a specific cinema — prepend only the language
    const pathSegments = to.path.split("/").filter(Boolean);
    const firstSegment = pathSegments[0];

    if (isValidCinemaSlug(firstSegment)) {
      const lang = lastVisitedLang.value ?? DEFAULT_CINEMA_LANGUAGE;
      const restAfterCinema =
        pathSegments.length > 1 ? `/${pathSegments.slice(1).join("/")}` : "";
      return navigateTo({
        path: `/${lang}/${firstSegment}${restAfterCinema}`,
        query: createRedirectQuery(to.query, false),
        hash: to.hash,
      });
    }

    if (cookiePath) {
      return navigateTo({
        path: `${cookiePath}${restOfPath}`,
        query: createRedirectQuery(to.query, false),
        hash: to.hash,
      });
    } else if (to.path !== "/") {
      return navigateTo({
        path: `/${
          lastVisitedLang.value ?? DEFAULT_CINEMA_LANGUAGE
        }/${DEFAULT_CINEMA_HANDLER}${restOfPath}`,
        query: createRedirectQuery(to.query, true),
        hash: to.hash,
      });
    }
  } else if (
    (hasInvalidLanguage || hasInvalidCinemaSlug) &&
    to.path?.length < 1000
  ) {
    if (cookiePath) {
      return navigateTo({
        path: `${cookiePath}${restOfPath}`,
        query: createRedirectQuery(to.query, false),
        hash: to.hash,
      });
    } else if (to.path !== "/") {
      return navigateTo({
        path: `/${
          lastVisitedLang.value ?? DEFAULT_CINEMA_LANGUAGE
        }/${DEFAULT_CINEMA_HANDLER}${restOfPath}`,
        query: createRedirectQuery(to.query, true),
        hash: to.hash,
      });
    }
  }
});
